import { isUndefined } from "lodash";
import { number } from "prop-types";
import React from "react";
import ReactDOMServer from "react-dom/server";

import BlogBanner from "../components/blog/blogBanner";
import BlogFancyCtaBlock from "../components/blog/blogFancyCtaBlock";
import BlogQuoteBlock from "../components/blog/blogQuoteBlock";
import CurrencyMapping, {
  CurrencyMappingAlt
} from "../components/text-page/currencyMapping";
import PeopleDollar from "../images/currencies/People_dollar.svg";
import PeopleEuro from "../images/currencies/People_euro.svg";
import PeoplePound from "../images/currencies/People_pound.svg";
import PeopleRand from "../images/currencies/People_rand.svg";
import PlanetDollar from "../images/currencies/Planet_dollar.svg";
import PlanetEuro from "../images/currencies/Planet_euro.svg";
import PlanetPound from "../images/currencies/Planet_pound.svg";
import PlanetRand from "../images/currencies/Planet_rand.svg";
import ArrowRight from "../images/icons/arrow-right.inline.svg";
import heroImgPlaceholder from "../images/volunteer/hero-img.png";
import { Link } from "../utils/customLink";
import { dateFormat } from "../utils/dateFormat";

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const formatContent = ({
  content,
  planetDomain,
  peopleDomain,
  currentBrand
}) => {
  if (isUndefined(content)) return "";

  return content
    ?.replace(/font-weight: 400;/g, "")
    ?.replace(
      /https?:\/\/www.gvi.co.uk/g,
      currentBrand === "people" ? planetDomain : ""
    )
    .replace(
      /https?:\/\/people.gvi.co.uk/g,
      currentBrand === "planet" ? peopleDomain : ""
    );
};

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const shortcodePreProcessor = ({
  programFees,
  currentSiteMeta,
  blogPostAcfFields,
  scholarships,
  description,
  universityName = ""
}) => {
  if (description === undefined || description === null || description === "")
    return description;

  const appProcessFee = programFees?.ppAppProcessFee || "";
  const holdingFee = programFees?.ppHoldingFee || "";
  const flexiDeposit = programFees?.ppFlexiDeposit || "";
  const flexiFee = programFees?.ppFlexiFee || "";
  const installmentDeposit = programFees?.ppInstallmentDeposit || "";
  const installmentFee = programFees?.ppInstallmentFee || "";
  const latePayment = programFees?.latePaymentFee || "";

  const programDeposit = programFees?.programDepositFee || "";
  const programMinFee = programFees?.programMinimumFee || "";
  const refundFees = programFees?.refundFee || "";
  const transferFeeLong = programFees?.transferFeeLongDuration || "";
  const transferFeeMedium = programFees?.transferFeeMediumDuration || "";
  const transferFeeShort = programFees?.transferFeeShortDuration || "";
  const fundraisingFees = programFees?.fundraisingFee || "";
  const maximumLiabilityFees = programFees?.maximumLiabilityFee || "";
  const cancellationWaiverFees = programFees?.cwRangeFee || "";
  const referFriendFees = programFees?.scpReferFriendEarning || "";
  const referFriendFlexFees = programFees?.scpReferFriendEarningFlex || "";
  const comboDiscountFees = programFees?.scpComboDiscount || "";
  const ingosupportFees = programFees?.ppIngoSupportFee || "";
  const onlineProgramScholarship = programFees || "";
  const returneeVoucherDiscountFees =
    programFees?.scpAlumniReturneeVoucher || "";
  const grantPriceConfigAmount = programFees?.grantsDiscounts || "";
  const foundationGrantPriceConfigAmount =
    programFees?.gviFoundationGrant || "";

  const ppAppProcessFee = () =>
    appProcessFee && (
      <CurrencyMapping
        programFee={appProcessFee}
        programFeeTitle={"ppAppProcessFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const ppHoldingFee = () =>
    holdingFee && (
      <CurrencyMapping
        programFee={holdingFee}
        programFeeTitle={"ppHoldingFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const ppFlexiDeposit = () =>
    flexiDeposit && (
      <CurrencyMapping
        programFee={flexiDeposit}
        programFeeTitle={"ppFlexiDeposit"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const ppFlexiFee = () =>
    flexiFee && (
      <CurrencyMapping
        programFee={flexiFee}
        programFeeTitle={"ppFlexiFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const ppInstallmentDeposit = () =>
    installmentDeposit && (
      <CurrencyMapping
        programFee={installmentDeposit}
        programFeeTitle={"ppInstallmentDeposit"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const ppInstallmentFee = () =>
    installmentFee && (
      <CurrencyMapping
        programFee={installmentFee}
        programFeeTitle={"ppInstallmentFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const latePaymentFee = () =>
    latePayment && (
      <CurrencyMapping
        programFee={latePayment}
        programFeeTitle={"latePaymentFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const refundFee = () =>
    refundFees && (
      <CurrencyMapping
        programFee={refundFees}
        programFeeTitle={"refundFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const programDepositFee = () =>
    programDeposit && (
      <CurrencyMapping
        programFee={programDeposit}
        programFeeTitle={"programDepositFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const programMinimumFee = () =>
    programMinFee && (
      <CurrencyMapping
        programFee={programMinFee}
        programFeeTitle={"programMinimumFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const transferFeeLongDuration = () =>
    transferFeeLong && (
      <CurrencyMapping
        programFee={transferFeeLong}
        programFeeTitle={"transferFeeLongDuration"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const transferFeeMediumDuration = () =>
    transferFeeMedium && (
      <CurrencyMapping
        programFee={transferFeeMedium}
        programFeeTitle={"transferFeeMediumDuration"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const transferFeeShortDuration = () =>
    transferFeeShort && (
      <CurrencyMapping
        programFee={transferFeeShort}
        programFeeTitle={"transferFeeShortDuration"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const fundraisingFee = () =>
    fundraisingFees && (
      <CurrencyMapping
        programFee={fundraisingFees}
        programFeeTitle={"fundraisingFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const maximumLiabilityFee = () =>
    maximumLiabilityFees && (
      <CurrencyMapping
        programFee={maximumLiabilityFees}
        programFeeTitle={"maximumLiabilityFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const cancellationWaiverFee = () =>
    cancellationWaiverFees && (
      <CurrencyMapping
        programFee={cancellationWaiverFees}
        programFeeTitle={"cwRangeFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const ReferFriendFee = () =>
    referFriendFees && (
      <CurrencyMapping
        programFee={referFriendFees}
        programFeeTitle={"scpReferFriendEarning"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const ReferFriendFlexFee = () =>
    referFriendFlexFees && (
      <CurrencyMapping
        programFee={referFriendFlexFees}
        programFeeTitle={"scpReferFriendEarningFlex"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const ComboDiscountFee = () =>
    comboDiscountFees && (
      <CurrencyMapping
        programFee={comboDiscountFees}
        programFeeTitle={"scpComboDiscount"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const IngoSupportFee = () =>
    ingosupportFees && (
      <CurrencyMapping
        programFee={ingosupportFees}
        programFeeTitle={"ppIngoSupportFee"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const onlineProgramScholarshipDiscount = () =>
    onlineProgramScholarship && (
      <CurrencyMapping
        programFee={onlineProgramScholarship}
        programFeeTitle={"osdDiscount"}
        currentSiteMeta={currentSiteMeta}
      />
    );
  const returneeVoucherDiscount = () =>
    returneeVoucherDiscountFees && (
      <CurrencyMapping
        programFee={returneeVoucherDiscountFees}
        programFeeTitle={"scpAlumniReturneeVoucher"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const grantPriceAmount = () =>
    grantPriceConfigAmount && (
      <CurrencyMapping
        programFee={grantPriceConfigAmount}
        programFeeTitle={"osdGrant"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const foundationGrant = () =>
    foundationGrantPriceConfigAmount && (
      <CurrencyMappingAlt
        programFee={foundationGrantPriceConfigAmount}
        programFeeTitle={"foundationGrant"}
        currentSiteMeta={currentSiteMeta}
      />
    );

  const LastDayOfMonth = () =>
    dateFormat(
      {
        day: "numeric",
        month: "short"
      },
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
    );

  const OfferDate = () =>
    dateFormat(
      {
        month: "long",
        year: "numeric"
      },
      new Date()
    );

  const formatWebinarUniversityName = () => {
    return universityName
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const blogQuoteBlock = () =>
    blogPostAcfFields?.blogBquoteSummary && (
      <div className="relative">
        <BlogQuoteBlock post={blogPostAcfFields} />
      </div>
    );
  const blogBanner = () =>
    blogPostAcfFields?.blogIncontentBannerText && (
      <BlogBanner post={blogPostAcfFields} scholarships={scholarships} />
    );
  const blogFancyCtaBlock = () =>
    blogPostAcfFields?.blogFancyCtaSummary && (
      <BlogFancyCtaBlock post={blogPostAcfFields} />
    );
  const blogProgramCtaBlock = () =>
    blogPostAcfFields?.blogProgramsButtonLink && (
      <div className="py-5">
        <Link
          className="flex !justify-center sm:justify-between items-center sm:w-1/4 py-2 px-5 planet:px-4 text-mob planet:bg-green-standard bg-red-light sm:text-base font-bold !text-white planet:!text-gray-darker rounded-xxxxl planet:rounded-sm"
          to={blogPostAcfFields?.blogProgramsButtonLink}
        >
          {blogPostAcfFields?.blogProgramsButtonTitle}
          <span className="flex items-center pl-2">
            <ArrowRight className="stroke-white planet:stroke-gray-darker" />
          </span>
        </Link>
      </div>
    );

  const shortcodeMapping = {
    SHORTCODE_AppProcessFee: ppAppProcessFee,
    SHORTCODE_HoldingFee: ppHoldingFee,
    SHORTCODE_FlexiDeposit: ppFlexiDeposit,
    SHORTCODE_FlexiFee: ppFlexiFee,
    SHORTCODE_InstallmentDeposit: ppInstallmentDeposit,
    SHORTCODE_InstallmentFee: ppInstallmentFee,
    SHORTCODE_LatePaymentFee: latePaymentFee,
    SHORTCODE_RefundFee: refundFee,
    SHORTCODE_CancellationWaiver: cancellationWaiverFee,
    SHORTCODE_ProgramDepositFee: programDepositFee,
    SHORTCODE_ProgramMinimumFee: programMinimumFee,
    SHORTCODE_TransferFeeLongDuration: transferFeeLongDuration,
    SHORTCODE_TransferFeeMediumDuration: transferFeeMediumDuration,
    SHORTCODE_TransferFeeShortDuration: transferFeeShortDuration,
    SHORTCODE_FundraisingFee: fundraisingFee,
    SHORTCODE_MaximumLiabilityFee: maximumLiabilityFee,
    SHORTCODE_ReferFriendEarning: ReferFriendFee,
    SHORTCODE_FlexReferFriendEarning: ReferFriendFlexFee,
    SHORTCODE_ComboDiscount: ComboDiscountFee,
    SHORTCODE_INGOSupportFee: IngoSupportFee,
    SHORTCODE_Quote: blogQuoteBlock,
    SHORTCODE_Banner: blogBanner,
    SHORTCODE_FancyCta: blogFancyCtaBlock,
    SHORTCODE_ProgramCta: blogProgramCtaBlock,
    SHORTCODE_OnlineScholarshipDiscount: onlineProgramScholarshipDiscount,
    SHORTCODE_Returnee_Voucher: returneeVoucherDiscount,
    SHORTCODE_GrantAmount: grantPriceAmount,
    OFFER_DATE: OfferDate,
    SHORTCODE_LastDayOfMonth: LastDayOfMonth,
    SHORTCODE_FoundationGrantAmount: foundationGrant,
    "[SHORTCODE_UniversityName]": formatWebinarUniversityName
  };

  let result = description;

  try {
    for (let shortcode in shortcodeMapping) {
      if (result.indexOf(shortcode) > -1) {
        const shortcodeMarkup = ReactDOMServer.renderToStaticMarkup(
          shortcodeMapping[shortcode]()
        );

        result = result.replaceAll(shortcode, shortcodeMarkup);
      }
    }
  } catch (error) {
    console.error("shortcode pre processor : ERROR  ", error);
  }

  return result;
};

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const renderCurrencyDateImage = ({ currentSiteMeta, currentBrand }) => {
  const currency = currentSiteMeta.currency;

  let icon;

  if (currentBrand.includes("planet")) {
    switch (currency?.toLowerCase()) {
      case "gbp":
        icon = PlanetPound;
        break;
      case "usd":
      case "cad":
      case "aud":
        icon = PlanetDollar;
        break;
      case "eur":
        icon = PlanetEuro;
        break;
      case "zar":
        icon = PlanetRand;
        break;
      default:
    }
  }

  if (currentBrand.includes("people")) {
    switch (currency?.toLowerCase()) {
      case "gbp":
        icon = PeoplePound;
        break;
      case "usd":
      case "cad":
      case "aud":
        icon = PeopleDollar;
        break;
      case "eur":
        icon = PeopleEuro;
        break;
      case "zar":
        icon = PeopleRand;
        break;
      default:
    }
  }

  return icon;
};

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const formatCurrencyAmount = ({
  currentSiteMeta,
  amount,
  currency = "gbp"
}) => {
  if (!amount || amount === undefined) return "";

  const currencyFormat = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency:
      currentSiteMeta?.currency ??
      process.env.GATSBY_PROJECT_CURRENCY ??
      currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: "code"
  });

  const cleanAmount =
    typeof amount === number
      ? amount
      : amount?.length >= 3
        ? parseInt(amount?.replaceAll(",", ""))
        : parseInt(amount);

  const currencyFormatAmount =
    cleanAmount && cleanAmount > 0 ? currencyFormat.format(cleanAmount) : 0;

  return currencyFormatAmount
    ?.replaceAll(currentSiteMeta?.currency, currentSiteMeta?.currency_symbol)
    ?.replaceAll(/\s/g, "");
};

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const formatNumberNoCurrency = (amount) => {
  if (!amount || amount === undefined) return "";

  const numberFormat = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: "code"
  });
  return numberFormat.format(amount);
};

/**
 * This function formats content by replacing the planet domain with the people domain.
 * @param {Object} obj - Object containing the content to be formatted, the planet domain, the people domain and the current brand.
 * @param {string} obj.content - The content to be formatted.
 * @param {string} obj.planetDomain - The planet domain.
 * @param {string} obj.peopleDomain - The people domain.
 * @param {string} obj.currentBrand - The current brand.
 * @returns {string} The formatted content.
 */

export const formatHeroBackgroundOptions = (config) => {
  if (!config) return {};

  const heroGatsbyBackgroundImage =
    config?.backgroundImage && config?.backgroundImage?.gatsbyImage
      ? config?.backgroundImage?.gatsbyImage
      : null;

  const heroGatsbyBackgroundImageMobile =
    config?.backgroundImageMobile && config?.backgroundImageMobile?.gatsbyImage
      ? config?.backgroundImageMobile?.gatsbyImage
      : heroGatsbyBackgroundImage;

  const heroDesktopImage =
    config?.backgroundImage &&
    config?.backgroundImage?.localFile &&
    config?.backgroundImage?.localFile?.extension
      ? config?.backgroundImage?.localFile?.publicURL
      : config && config?.backgroundImage && config?.backgroundImage?.sourceUrl
        ? config?.backgroundImage?.sourceUrl
        : "";
  const heroMobileImage =
    config?.backgroundImageMobile &&
    config?.backgroundImageMobile?.localFile &&
    config?.backgroundImageMobile?.localFile?.extension
      ? config?.backgroundImageMobile?.localFile?.publicURL
      : config?.backgroundImageMobile &&
          config?.backgroundImageMobile?.sourceUrl
        ? config?.backgroundImageMobile?.sourceUrl
        : "";

  const heroBackgroundVideo = config?.backgroundVideoJw
    ? config?.backgroundVideoJw
    : "";

  return {
    image: {
      mobile: heroMobileImage || heroDesktopImage || heroImgPlaceholder,
      desktop: heroDesktopImage || heroImgPlaceholder
    },
    gatsby_image: {
      mobile: heroGatsbyBackgroundImageMobile || "",
      desktop: heroGatsbyBackgroundImage || ""
    },
    video: {
      desktop: heroBackgroundVideo || "",
      mobile: ""
    }
  };
};
